'use client';

import { useRouter } from '@bprogress/next/app';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useRef } from 'react';

interface NavigationObserver {
  pathname: string;
  resolve: () => void;
}

/**
 * Simple wrapper around Next.js router to allow synchronous navigation.
 * It allows you to wait for the navigation to complete before proceeding.
 */
export function useSyncRouter() {
  const router = useRouter();
  const pathname = usePathname();
  const observers = useRef<NavigationObserver[]>([]);

  const push = useCallback(
    (href: string) => {
      return new Promise<void>((resolve) => {
        observers.current.push({
          pathname: href,
          resolve,
        });
        router.push(href);
      });
    },
    [router],
  );

  useEffect(() => {
    const currentObservers = observers.current.filter((o) => o.pathname === pathname);
    observers.current = observers.current.filter((o) => o.pathname !== pathname);
    currentObservers.forEach((observer) => {
      observer.resolve();
    });
  }, [pathname]);

  return { push, pathname };
}
